import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Play, Clock, Building, Users, Coffee, Heart, Bus, Phone, Mail } from "lucide-react";
import { useState } from "react";

interface OfficeLocation {
  name: string;
  address: string;
  city: string;
  state: string;
  videoUrl: string;
  features: string[];
}

const officeLocations: OfficeLocation[] = [
  {
    name: "Mumbai Office",
    address: "Mumbai",
    city: "Maharashtra",
    state: "India",
    videoUrl: "https://storage.googleapis.com/sfy-launchpad-cda14.firebasestorage.app/office-videos/mumbai-office-tour.mp4?GoogleAccessId=firebase-admin-full%40sfy-launchpad-cda14.iam.gserviceaccount.com&Expires=3336639428&Signature=OYFK6kF8GkO%2BbFNoYRLl28wU226UmEgzECx99kdea4x2PG2OF%2BYT8NGItGKE9jt9YBGx2dLmqm3SMaPYZrLkHxP4GhhYwePOHuBvcUSVAgfIS7zvll687RikbWSvJHRBxDopWPfmzkNbTrKvXE2V3odnr8bl18ECgduYEN%2Fgb8Moa%2FxpPD7yEOfaiKyXJ4le%2Fj4GNGf%2BiRLvfrle80x1Xh0oPAufaSaAlqc%2Bj4%2FIEyGlEPuTdNSgD67f0YkTD4ADQeE38kj5yEdhFQGgAtVoxAb4euqgwuwbpxudKv8Qrw%2FvfHBgUidP0fvRAw5k%2BUOLKaVYRnDUQcVIEqKQXH4hHg%3D%3D",
    features: [
      "Collaborative workspace",
      "Meeting rooms",
      "Cafeteria",
      "Public transport access"
    ]
  },
  {
    name: "Bangalore Office",
    address: "Bangalore",
    city: "Karnataka",
    state: "India",
    videoUrl: "/BangaloreOffice.mp4",
    features: [
      "Collaborative workspace",
      "Meeting rooms",
      "Cafeteria",
      "Public transport access"
    ]
  }
];

export default function OfficeLocations() {
  const [, setLocation] = useLocation();
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<string>("");

  const handleVideoClick = (videoUrl: string) => {
    setCurrentVideo(videoUrl);
    setIsVideoOpen(true);
  };

  return (
    <div className="min-h-screen font-muli">
      {/* Header */}
      <div className="shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-servify-purple">Office Locations</h1>
              <p className="text-gray-600 mt-1">Take a virtual tour of our offices across India</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Office Locations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {officeLocations.map((office, index) => (
            <Card 
              key={index} 
              className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white"
            >
              <CardContent className="p-6">
                {/* Office Header */}
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-servify-purple" />
                  <h3 className="text-xl font-bold text-servify-purple">{office.name}</h3>
                </div>
                

                
                {/* Video Player */}
                <div 
                  className="relative mb-4 bg-gray-100 rounded-lg h-48 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => handleVideoClick(office.videoUrl)}
                >
                  <div className="w-16 h-16 bg-servify-alt-green rounded-full flex items-center justify-center hover:bg-servify-alt-green/80 transition-colors">
                    <Play className="h-8 w-8 text-white ml-1" />
                  </div>
                  {/* Video controls placeholder */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-xs">
                    <div className="flex items-center gap-2">
                      <Play className="h-3 w-3" />
                      <span>Watch Office</span>
                    </div>
                  </div>
                </div>
                
                {/* Watch Office Tour Button */}
                <Button 
                  className="w-full bg-servify-purple hover:bg-servify-purple-dark text-white mb-6"
                  onClick={() => handleVideoClick(office.videoUrl)}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Watch Office Tour
                </Button>
                
                {/* Features */}
                <div className="mb-6">
                  <h4 className="font-semibold text-servify-dark-grey mb-3">Features</h4>
                  <div className="space-y-2">
                    {office.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-servify-alt-green rounded-full"></div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Contact Section */}
                <div className="text-left">
                  <h4 className="font-semibold text-servify-dark-grey mb-3">Contact</h4>
                  
                  {/* Address */}
                  <div className="flex items-start gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-servify-alt-green mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-gray-600">
                      <p className="leading-relaxed">
                        {office.name === "Mumbai Office" 
                          ? "1022, Building 10, Second Floor, Solitaire Corporate Park, Chakala, Andheri East, Mumbai, Maharashtra 400093."
                          : "GF-1, Obeya Pulse, 7/1, Ulsoor Rd, Halasuru, Yellappa Chetty Layout, Sivanchetti Gardens, Bengaluru, Karnataka 560042"
                        }
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Office Hours: 10:30 AM to 7:30 PM
                      </p>
                    </div>
                  </div>
                  
                  {/* Phone */}
                  <div className="flex items-center gap-2 mb-3">
                    <Phone className="w-4 h-4 text-servify-alt-green flex-shrink-0" />
                    <span className="text-sm text-gray-600">1800 123 333 888</span>
                  </div>
                  
                  {/* Email */}
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-servify-alt-green flex-shrink-0" />
                    <span className="text-sm text-gray-600">info@servify.com</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Video Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-hidden relative">
            {/* Modal Header */}
            <div className="bg-servify-purple text-white p-6 relative">
              <h2 className="text-2xl font-bold text-center">Office Tour</h2>
              <p className="text-servify-purple/20 text-center mt-2">Virtual Office Experience</p>
              
              {/* Close Button */}
              <button
                onClick={() => {
                  setIsVideoOpen(false);
                  setCurrentVideo("");
                }}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Video Content */}
            <div className="p-6 flex flex-col items-center">
              <video 
                controls
                className="w-full h-auto max-w-full rounded-xl shadow-lg"
                autoPlay
                style={{ maxHeight: '60vh' }}
              >
                <source src={currentVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
